import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BLACK_FILL_SENTIMENT_LABELS, SENTIMENT_ICON_BASE_PATH, SENTIMENT_ICON_MAP, SENTIMENT_MAPPING } from '../sentiment-legend/sentiment-legend.model';
import { SentimentParams, SentimentRange } from './cxone-sentiment-config.model';

@Component({
    selector: 'cxone-sentiment-cell-renderer',
    templateUrl: './cxone-sentiment-cell-renderer.component.html',
    styleUrl: './cxone-sentiment-cell-renderer.component.scss',
})
export class CxoneSentimentCellRendererComponent implements ICellRendererAngularComp {

    public params: ICellRendererParams;
    public cellBackgroundColor = '';
    public sentimentEmojiIcon: string | null = null;
    public currentSentimentLabel = '';

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.updateCellRendering();
    }

    refresh(params: ICellRendererParams): boolean {
        this.params = params;
        this.updateCellRendering();
        return true;
    }

    private updateCellRendering(): void {
        this.determineBackgroundColor();
        this.determineSentimentDisplay();
    }

    private determineSentimentDisplay(): void {
        const { useSentimentColors, showEmojis, sentimentRanges } = this.getSentimentParams();

        if (this.shouldShowSentimentEmoji(showEmojis, useSentimentColors, sentimentRanges)) {
            const numericValue = this.params?.value;

            if (!isNaN(numericValue)) {
                const matchingRange = this.findMatchingRange(sentimentRanges, numericValue);

                if (matchingRange) {
                    this.setSentimentEmoji(sentimentRanges, matchingRange);
                } else {
                    this.clearSentimentEmoji();
                }
            } else {
                this.clearSentimentEmoji();
            }
        } else {
            this.clearSentimentEmoji();
        }
    }

    private getSentimentParams(): SentimentParams {
        return {
            useSentimentColors: (this.params as any).useSentimentColors,
            showEmojis: (this.params as any).showEmojis,
            sentimentRanges: (this.params as any).sentimentRanges || []
        };
    }

    private shouldShowSentimentEmoji(showEmojis: boolean, useSentimentColors: boolean, sentimentRanges: SentimentRange[]): boolean {
        return showEmojis && useSentimentColors && Array.isArray(sentimentRanges) && sentimentRanges.length > 0 && this.params?.value != null;
    }

    private findMatchingRange(sentimentRanges: SentimentRange[], numericValue: number): SentimentRange | undefined {
        return sentimentRanges.find(range =>
            numericValue >= range.left && numericValue <= range.right
        );
    }

    private setSentimentEmoji(sentimentRanges: SentimentRange[], matchingRange: SentimentRange): void {
        const dynamicLabel = this.getDynamicSentimentLabel(sentimentRanges, matchingRange);
        this.currentSentimentLabel = dynamicLabel;
        this.sentimentEmojiIcon = this.getSentimentIcon(dynamicLabel);
    }

    private clearSentimentEmoji(): void {
        this.sentimentEmojiIcon = null;
        this.currentSentimentLabel = '';
    }

    private getDynamicSentimentLabel(ranges: SentimentRange[], matchingRange: SentimentRange): string {
        // If the range already has a label, use it
        if (matchingRange.label) {
            return matchingRange.label;
        }

        // Sort ranges by left value to determine position
        const sortedRanges = [...ranges].sort((a, b) => a.left - b.left);
        const rangeIndex = sortedRanges.findIndex(range =>
            range.left === matchingRange.left && range.right === matchingRange.right
        );

        return this.getSentimentLabelTextByRangeIndex(sortedRanges.length, rangeIndex);
    }

    private getSentimentLabelTextByRangeIndex(totalRanges: number, rangeIndex: number): string {
        const labels = SENTIMENT_MAPPING[totalRanges] || SENTIMENT_MAPPING[3];
        const rangeLabels = labels.map(record => record.label);
        return rangeLabels[rangeIndex];
    }

    private getSentimentIcon(label: string): string | null {
        const iconPath = SENTIMENT_ICON_MAP[label];
        return iconPath ? `${SENTIMENT_ICON_BASE_PATH}${iconPath}` : null;
    }

    getSentimentIconStyle(label: string): any {
        return BLACK_FILL_SENTIMENT_LABELS.includes(label)
            ? { 'filter': 'brightness(0)' }
            : { 'filter': 'none' };
    }

    private determineBackgroundColor(): void {
        const { useSentimentColors, sentimentRanges } = this.getSentimentParams();
        const customBackgroundColor = (this.params as any).backgroundColor;

        if (this.canApplySentimentColors(useSentimentColors, sentimentRanges)) {
            const numericValue = this.params.value;

            if (!isNaN(numericValue)) {
                const matchingRange = this.findMatchingRange(sentimentRanges, numericValue);
                this.cellBackgroundColor = matchingRange?.color || '';
            } else {
                this.cellBackgroundColor = '';
            }
        } else if (customBackgroundColor) {
            this.cellBackgroundColor = customBackgroundColor;
        } else {
            this.cellBackgroundColor = '';
        }
    }

    private canApplySentimentColors(useSentimentColors: boolean, sentimentRanges: SentimentRange[]): boolean {
        return useSentimentColors && Array.isArray(sentimentRanges) && sentimentRanges.length > 0 && this.params?.value != null;
    }
}
